export let canvas               = null;
export let layout               = null;
export let canvasIds            = [];
export let currentObjects       = [];
export let currentBackground    = {};

export function setGlobals(globalCanvas, globalLayout, globalCanvasIds, globalCurrentObjects, globalCurrentBackground) {
    canvas              = globalCanvas;
    layout              = globalLayout;
    canvasIds           = globalCanvasIds;
    currentObjects      = globalCurrentObjects;
    currentBackground   = globalCurrentBackground;
}

export function setupPanning () {
    clearCanvasEvents();
    disableCanvasActions();
    toggleCrosshairLines(false);

    canvas.defaultCursor = "grab";
    canvas.getObjects().forEach(obj => { obj.set({ hoverCursor: "grab" })});

    layout.value.canvas.tools.panning = false;
    let lastPosX = 0;
    let lastPosY = 0;
    let isDragging = false;
    let initialDistance = 0;
    let isZooming = false;
    let lastTouchTime = 0;
    let tappedTwice = false;
    let touchStarted = false;

    const getPinchDistance = (touch1, touch2) => {
        return Math.sqrt(
            Math.pow(touch2.clientX - touch1.clientX, 2) + 
            Math.pow(touch2.clientY - touch1.clientY, 2)
        );
    };

    const getPinchCenter = (touch1, touch2) => {
        return {
            x: (touch1.clientX + touch2.clientX) / 2,
            y: (touch1.clientY + touch2.clientY) / 2
        };
    };

    const isPanningMode = () => {
        return layout.value.canvas.tools.currentTool === 'panning';
    };

    const handleTap = (e) => {
        if (!isPanningMode()) return;
        
        const now = Date.now();
        const timeSince = now - lastTouchTime;
        
        if (timeSince < 300 && timeSince > 0) {
            tappedTwice = true;
            
            const currentZoom = canvas.getZoom();
            const newZoom = currentZoom > 1 ? 1 : 1.5;
            
            const canvasRect = canvas.getElement().getBoundingClientRect();
            const zoomPoint = new fabric.Point(
                e.clientX - canvasRect.left,
                e.clientY - canvasRect.top
            );
            
            canvas.zoomToPoint(zoomPoint, newZoom);
            
            layout.value.canvas.tools.zoom = newZoom;
            layout.value.canvas.viewport = {
                zoom: newZoom,
                transform: [...canvas.viewportTransform],
                persistEnabled: layout.value.canvas.viewport.persistEnabled
            };
        } else {
            tappedTwice = false;
            setTimeout(() => {
                if (!tappedTwice && isPanningMode()) {
                    const canvasRect = canvas.getElement().getBoundingClientRect();
                    const pointer = new fabric.Point(
                        e.clientX - canvasRect.left,
                        e.clientY - canvasRect.top
                    );
                    const objects = canvas.getObjects();
                    
                    for (let i = objects.length - 1; i >= 0; i--) {
                        if (objects[i].containsPoint(pointer) && objects[i].selectable) {
                            canvas.setActiveObject(objects[i]);
                            canvas.renderAll();
                            break;
                        }
                    }
                }
            }, 300);
        }
        
        lastTouchTime = now;
    };

    canvas.wrapperEl.addEventListener('touchstart', function(e) {
        touchStarted = true;
        
        // Only prevent default in panning mode or for multi-touch
        if (isPanningMode() || e.touches.length > 1) {
            e.preventDefault();
        }
        
        // Only handle pinch zoom in panning mode
        if (e.touches.length > 1 && isPanningMode()) {
            initialDistance = getPinchDistance(e.touches[0], e.touches[1]);
            isZooming = true;
            return;
        }
        
        if (isPanningMode()) {
            isDragging = true;
            lastPosX = e.touches[0].clientX;
            lastPosY = e.touches[0].clientY;
            layout.value.canvas.tools.panning = true;
        }
    }, { passive: false });

    canvas.wrapperEl.addEventListener('touchmove', function(e) {
        if (!touchStarted) return;
        
        // Only prevent default in panning mode or for multi-touch
        if (isPanningMode() || e.touches.length > 1) {
            e.preventDefault();
        }
        
        // Only handle pinch zoom in panning mode
        if (e.touches.length === 2 && isZooming && isPanningMode()) {
            const currentDistance = getPinchDistance(e.touches[0], e.touches[1]);
            const center = getPinchCenter(e.touches[0], e.touches[1]);
            
            if (initialDistance > 0) {
                const scale = currentDistance / initialDistance;
                const currentZoom = canvas.getZoom();
                let newZoom = currentZoom * scale;
                
                const baseScale = layout.value.canvas.viewport?.baseScale || currentZoom;
                const minZoom = baseScale * 0.2;
                const maxZoom = baseScale * 3;
                
                newZoom = Math.min(Math.max(minZoom, newZoom), maxZoom);
                
                const canvasRect = canvas.getElement().getBoundingClientRect();
                const zoomPoint = new fabric.Point(
                    center.x - canvasRect.left,
                    center.y - canvasRect.top
                );
                
                canvas.zoomToPoint(zoomPoint, newZoom);
                
                layout.value.canvas.tools.zoom = newZoom;
                layout.value.canvas.viewport = {
                    zoom: newZoom,
                    transform: [...canvas.viewportTransform],
                    persistEnabled: layout.value.canvas.viewport.persistEnabled,
                    baseScale: baseScale
                };
                
                initialDistance = currentDistance;
            }
            return;
        }
        
        if (!isDragging || isZooming || e.touches.length !== 1 || !isPanningMode()) return;
        
        toggleCrosshairLines(false);
        
        const currentX = e.touches[0].clientX;
        const currentY = e.touches[0].clientY;
        
        const deltaX = currentX - lastPosX;
        const deltaY = currentY - lastPosY;
        
        lastPosX = currentX;
        lastPosY = currentY;
        
        const delta = new fabric.Point(deltaX, deltaY);
        canvas.relativePan(delta);
    }, { passive: false });

    canvas.wrapperEl.addEventListener('touchend', function(e) {
        if (!touchStarted) return;
        
        if (e.touches.length === 0) {
            if (!isZooming && e.changedTouches.length === 1 && isPanningMode()) {
                handleTap(e.changedTouches[0]);
            }
            
            touchStarted = false;
            isDragging = false;
            isZooming = false;
            layout.value.canvas.tools.panning = false;
            initialDistance = 0;
        }
    }, { passive: false });

    canvas.on('mouse:down', function(e) { 
        if (e.e.touches || !isPanningMode()) return;
        
        isDragging = true;
        lastPosX = e.e.clientX;
        lastPosY = e.e.clientY;
        layout.value.canvas.tools.panning = true;
    });

    canvas.on('mouse:move', function(e) {
        if (e.e.touches || !isDragging || !isPanningMode()) return;
        
        toggleCrosshairLines(false);
        
        const currentX = e.e.clientX;
        const currentY = e.e.clientY;
        
        const deltaX = currentX - lastPosX;
        const deltaY = currentY - lastPosY;
        
        lastPosX = currentX;
        lastPosY = currentY;
        
        const delta = new fabric.Point(deltaX, deltaY);
        canvas.relativePan(delta);
    });

    canvas.on('mouse:up', function(e) {
        if (e.e.touches) return;
        
        isDragging = false;
        layout.value.canvas.tools.panning = false;
    });
    
    canvas.on('mouse:wheel', function(opt) {
        const delta = opt.e.deltaY;
        let zoom = canvas.getZoom();
        zoom *= 0.999 ** delta;
        
        const baseScale = layout.value.canvas.viewport?.baseScale || zoom;
        const minZoom = baseScale * 0.2;
        const maxZoom = baseScale * 3;
        
        zoom = Math.min(Math.max(minZoom, zoom), maxZoom);
        
        const point = new fabric.Point(
            opt.e.offsetX,
            opt.e.offsetY
        );
        
        canvas.zoomToPoint(point, zoom);
        layout.value.canvas.tools.zoom = zoom;
        layout.value.canvas.viewport = {
            zoom: zoom,
            transform: [...canvas.viewportTransform],
            persistEnabled: layout.value.canvas.viewport.persistEnabled,
            baseScale: baseScale
        };
        
        opt.e.preventDefault();
        opt.e.stopPropagation();
    });
}

export function scrollToElement (uuid) {
    if (!uuid) return;
    
    const element = document.getElementById(`box-card-${uuid}`);
    if (!element) return;
    
    const container = document.getElementById('bounding-boxes-list');
    if (!container) return;
    
    element.scrollIntoView({ behavior: 'smooth' });
}

export function removeElement (uuid) {
    const objectsToRemove = canvas.getObjects().filter(obj => obj.uuid === uuid || obj.uuid_parent === uuid);
    
    if (objectsToRemove.length === 0) return;
    
    const isSegmentationPath = objectsToRemove.some(obj => 
        obj.type === 'path' || 
        (obj.elementType === 'mask' && layout.value.dataset.type === 'MULTILABEL')
    );
    
    if (isSegmentationPath) {
        const isMenuRemoval = objectsToRemove.some(obj => 
            obj.elementType === 'mask' || 
            (obj.type === 'path' && obj.uuid_parent)
        );
        
        const isRemovingAllMasks = window.__lastSnapshotBeforeRemoval && isMenuRemoval;
        
        if (isRemovingAllMasks) {
            canvas.historyUndo.push(JSON.parse(window.__lastSnapshotBeforeRemoval));
            window.__lastSnapshotBeforeRemoval = null;
        } else {
            canvas._pushHistory();
        }
        
        const wasRecording = canvas._historyRecording;
        canvas._historyRecording = false;
        
        objectsToRemove.forEach(obj => canvas.remove(obj));
        
        if (layout.value.dataset.type === 'MULTILABEL') {
            const updatedIds = canvasIds.value.filter(c => {
                return canvas.getObjects().some(obj => obj.uuid_parent === c.uuid);
            });
              
            updateCanvasIds(updatedIds);
            canvasIds.value.splice(0, canvasIds.value.length, ...updatedIds);
        }
        
        canvas._historyRecording = wasRecording;
    } else {
        objectsToRemove.forEach(obj => canvas.remove(obj));
        
        if (canvas._historyRecording) {
            canvas._pushHistory();
        }
    }
    
    canvas.renderAll();
}

export function clearCanvasEvents () {
    canvas.off('mouse:down');
    canvas.off('mouse:move');
    canvas.off('mouse:up');
    canvas.off('mouse:out');
    canvas.off('path:created');
    
    if (canvas._pathConfirmHandler) {
        window.removeEventListener('keydown', canvas._pathConfirmHandler);
        window.removeEventListener('mousedown', canvas._pathConfirmHandler);
        canvas._pathConfirmHandler = null;
    }
    
    if (canvas.wrapperEl) {
        canvas.wrapperEl.removeEventListener('touchstart', null);
        canvas.wrapperEl.removeEventListener('touchmove', null);
        canvas.wrapperEl.removeEventListener('touchend', null);
    }
}

export function disableCanvasActions () {
    canvas.selectable           = false;
    canvas.erasable             = false;
    canvas.isDrawingMode        = false;
    canvas.forEachObject(obj => {
        obj.set({
            selectable: false,
            evented:    false
        });
    });
}

export function toggleCrosshairLines (show = false, pointer = false) {
    canvas.getObjects().filter(obj => obj.elementType === 'crosshair').forEach(obj => canvas.remove(obj));

    if (show && pointer) {
        if (typeof pointer.x !== 'number' || typeof pointer.y !== 'number') {
            return;
        }

        const vpt = canvas.viewportTransform;
        if (!vpt) {
            return;
        }

        const zoom = vpt[0];
        if (zoom === 0) {
            return;
        }

        const panX = vpt[4];
        const panY = vpt[5];

        const canvasEl = canvas.getElement();
        if (!canvasEl) {
            return;
        }
        const screenWidth = canvasEl.clientWidth;
        const screenHeight = canvasEl.clientHeight;

        const x1 = -panX / zoom;
        const y1 = -panY / zoom;
        const x2 = (screenWidth - panX) / zoom;
        const y2 = (screenHeight - panY) / zoom;

        const buffer = 10 / zoom;

        const strokeWidthValue = 2 / zoom;
        const dashArrayValue = [8 / zoom, 4 / zoom];

        let lineX = new fabric.Line(
            [x1 - buffer, pointer.y, x2 + buffer, pointer.y],
            {
                stroke: 'white',
                strokeWidth: strokeWidthValue,
                strokeDashArray: dashArrayValue,
                selectable: false,
                elementType: 'crosshair',
                excludeFromExport: true,
                evented: false
            }
        );

        let lineY = new fabric.Line(
            [pointer.x, y1 - buffer, pointer.x, y2 + buffer],
            {
                stroke: 'white',
                strokeWidth: strokeWidthValue,
                strokeDashArray: dashArrayValue,
                selectable: false,
                elementType: 'crosshair',
                excludeFromExport: true,
                evented: false
            }
        );

        canvas.add(lineX, lineY);
        lineX.bringToFront();
        lineY.bringToFront();
    }

    canvas.renderAll();
}

export function updateCanvasIds () {
    const uniqueNames = [...new Set(canvasIds.value.map(canvas => canvas.name))];

    uniqueNames.forEach(name => {
        const canvasesWithName = canvasIds.value.filter(canvas => canvas.name === name);
        canvasesWithName.forEach((canvas, index) => {
            canvas.index = index + 1;
        });
    });
}

export function updateCanvasIdsFromPaths () {
    const pathTypes = new Set();

    canvas.getObjects().forEach(obj => {
        if ((obj.type === 'path' || obj.type === 'image' || obj.elementType === 'mask') && obj.id) {
            pathTypes.add(obj.id);
        }
    });

    pathTypes.forEach(typeName => {
        const pathOfType = canvas.getObjects().find(obj => (obj.type === 'path' || obj.type === 'image') && obj.id === typeName);
        if (pathOfType && pathOfType.uuid_parent) {
            const existingIndex = canvasIds.value.findIndex(item => item.uuid === pathOfType.uuid_parent);
            const newObject = {
                uuid:   pathOfType.uuid_parent,
                id:     pathOfType.id,
                name:   typeName,
                stroke: pathOfType.stroke || pathOfType.color || '#ff0000',
                type:   pathOfType.type === 'path' ? 'path' : 'image'
            };

            if (existingIndex > -1) {
                canvasIds.value[existingIndex] = newObject;
            } else {
                canvasIds.value.push(newObject);
            }
        }
    });

    if (pathTypes.size === 0) { canvasIds.value = []; }

    updateCanvasIds();
    canvas._pushHistory();
    canvas.renderAll();
}

export function updateCanvasIdsFromPaths_w () {
    const pathTypes = new Set();
    canvas.getObjects().forEach(obj => {
        if ((obj.type === 'path' || obj.type === 'image') && obj.name) {
            pathTypes.add(obj.name);
        }
    });

    if (pathTypes.size === 0) {
        canvasIds.value = [];
        canvas.renderAll();
        return;
    }

    pathTypes.forEach(typeName => {
        const pathOfType = canvas.getObjects().find(obj => 
            (obj.type === 'path' || obj.type === 'image') && obj.name === typeName
        );
        
        if (pathOfType && pathOfType.uuid_parent) {
            if (!canvasIds.value.some(canvas => canvas.uuid === pathOfType.uuid_parent)) {
                canvasIds.value.push({
                    uuid:   pathOfType.uuid_parent,
                    name:   typeName,
                    stroke: pathOfType.stroke || pathOfType.color || '#ff0000',
                    type:   pathOfType.type === 'path' ? 'path' : 'image'
                });
            }
        }
    });

    updateCanvasIds();
    canvas._pushHistory();
    canvas.renderAll();
}